import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminFetch } from './useAdminApi';

export interface AdminCustomerRow {
  qbo_customer_id: number;
  name: string;
  active_in_qbo: boolean;
  enabled: boolean;
  orders_customer_id: string | null;
  orders_active: boolean | null;
  /** Manual ordering hard-lock. null when not enabled; true => ordering paused. */
  on_credit_hold: boolean | null;
  /** Feature flag overrides. null when not enabled. Missing key => ON. */
  features: Record<string, boolean> | null;
  /** Invoice-delivery email opt-in. null when not enabled; default OFF. */
  invoice_emails_enabled: boolean | null;
  /** Per-customer billing-comms config. null when the customer isn't enabled. */
  billing_email: string | null;
  accounting_email: string | null;
  remittance_email: string | null;
  optional_email: string | null;
  reminders_enabled: boolean | null;
  statements_enabled: boolean | null;
  invoice_recipients: BillingRole[] | null;
  statement_recipients: BillingRole[] | null;
  reminder_recipients: BillingRole[] | null;
  order_update_recipients: BillingRole[] | null;
  paper_statements_enabled: boolean | null;
  paper_invoices_enabled: boolean | null;
  /** Manual "switched over to the new system" tracker. null when not enabled. */
  on_new_system: boolean | null;
  location_count: number;
}

export type BillingRole = 'billing' | 'accounting' | 'remittance' | 'optional';

export interface BillingCommsInput {
  customer_id: string;
  billing_email?: string | null;
  accounting_email?: string | null;
  remittance_email?: string | null;
  optional_email?: string | null;
  reminders_enabled?: boolean;
  statements_enabled?: boolean;
  invoice_recipients?: BillingRole[];
  statement_recipients?: BillingRole[];
  reminder_recipients?: BillingRole[];
  order_update_recipients?: BillingRole[];
  paper_statements_enabled?: boolean;
  paper_invoices_enabled?: boolean;
  billing_doc_type?: 'statement' | 'invoice' | 'both';
}

interface ListResponse {
  ok: true;
  count: number;
  limit: number;
  offset: number;
  q: string;
  rows: AdminCustomerRow[];
}

/** Lifecycle tab on /admin/customers: active = set up on web; inactive =
 * open QBO account never set up; closed = deactivated on web. */
export type CustomerLifecycle = 'active' | 'inactive' | 'closed';

export function useAdminCustomers(
  q: string,
  offset: number,
  limit: number = 50,
  status?: CustomerLifecycle,
) {
  return useQuery<ListResponse>({
    queryKey: ['admin', 'customers', status ?? 'all', q, offset, limit],
    queryFn: () =>
      adminFetch<ListResponse>(
        `admin-list-qbo-customers?q=${encodeURIComponent(q)}&offset=${offset}&limit=${limit}` +
          (status ? `&status=${status}` : ''),
      ),
    staleTime: 30 * 1000,
  });
}

export interface CollectionSeedResult {
  invoices_scanned: number;
  lines_scanned: number;
  catalog_matches: number;
  seeded: number;
  skipped_no_catalog_match: number;
}

export function useEnableCustomer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (qboCustomerId: number) =>
      adminFetch<{
        ok: true;
        customer_id: string;
        qbo_customer_id: number;
        name: string;
        locations_imported: number;
        already_enabled: boolean;
        collection_seed: CollectionSeedResult | null;
        collection_seed_error: string | null;
      }>('admin-enable-customer', {
        method: 'POST',
        body: JSON.stringify({ qbo_customer_id: qboCustomerId }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'customers'] });
    },
  });
}

export function useRefreshCustomerCollection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (customerId: string) =>
      adminFetch<{
        ok: true;
        customer_id: string;
        qbo_customer_id: number;
        name: string;
        seed: CollectionSeedResult;
      }>('admin-refresh-customer-collection', {
        method: 'POST',
        body: JSON.stringify({ customer_id: customerId }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'customers'] });
    },
  });
}

export function useSetCustomerActive() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { customer_id: string; active: boolean }) =>
      adminFetch<{ ok: true }>('admin-set-customer-active', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'customers'] });
    },
  });
}

export function useSetCreditHold() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { customer_id: string; on_credit_hold: boolean }) =>
      adminFetch<{ ok: true; customer_id: string; on_credit_hold: boolean }>(
        'admin-set-credit-hold',
        {
          method: 'POST',
          body: JSON.stringify(input),
        },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'customers'] });
    },
  });
}

export function useSetInvoiceEmails() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { customer_id: string; enabled: boolean }) =>
      adminFetch<{ ok: true; customer_id: string; invoice_emails_enabled: boolean }>(
        'admin-set-invoice-emails',
        {
          method: 'POST',
          body: JSON.stringify(input),
        },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'customers'] });
    },
  });
}

export function useSetBillingComms() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: BillingCommsInput) =>
      adminFetch<{ ok: true; customer_id: string }>('admin-set-billing-comms', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'customers'] });
    },
  });
}

export interface StatementSendResult {
  ok: true;
  customer_id: string;
  sent: boolean;
  open_count: number;
  open_total: number;
  resend_id: string | null;
  skipped_reason?: string;
  /** True when this went to a test address rather than the customer. */
  test_send?: boolean;
  recipients?: string[];
}

export function useSendStatement() {
  return useMutation({
    // `to` is a TEST send: the customer's real statement, delivered to those
    // addresses instead of the customer's billing recipients.
    mutationFn: ({ customerId, to }: { customerId: string; to?: string[] }) =>
      adminFetch<StatementSendResult>('admin-send-statement', {
        method: 'POST',
        body: JSON.stringify({ customer_id: customerId, ...(to?.length ? { to } : {}) }),
      }),
  });
}

export type CustomerFeatureKey =
  | 'cylinder_inventory'
  | 'service_requests'
  | 'pay_online'
  | 'card_on_file_exempt';

export function useSetCustomerFeatures() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      customer_id: string;
      features: Partial<Record<CustomerFeatureKey, boolean>>;
    }) =>
      adminFetch<{ ok: true; customer_id: string; features: Record<string, boolean> }>(
        'admin-set-customer-features',
        {
          method: 'POST',
          body: JSON.stringify(input),
        },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'customers'] });
    },
  });
}
